import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '@shared/shared.module';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';
import { ProjectSummaryDocumentsSectionsConfig } from '@modules/projects/shared/consts/project-summary-documents-sections.const';
import { EntryType } from '@shared/modules/entry/enums/entry-type.enum';
import { ProjectStoreService } from '@modules/projects/shared/services/project-store.service';
import { Entry } from '@shared/interfaces/entry.interface';
import { Project } from '@modules/projects/shared/models/project.model';
import { ProjectCreatorEntitySidenavController } from '@modules/project-creator/shared/controllers/project-creator-entity-sidenav.controller';
import {
  ProjectCreatorEntryService,
  ProjectEntryActionType
} from '@modules/project-creator/shared/services/project-creator-entry.service';
import { ProjectCreatorApiService } from '@modules/project-creator/shared/services/project-creator-api.service';
import { RemoveModalController } from '@shared/controllers/remove-modal.controller';
import { finalize } from 'rxjs';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { Attachment } from '@shared/interfaces/attachment.interface';
import { StorageService } from '@core/services/storage.service';
import { SwiperComponent, SwiperModule } from 'swiper/angular';
import { AttachmentService } from '@shared/services/attachment.service';
import { DocumentFileNameModalComponent } from './components/document-file-name-modal/document-file-name-modal.component';
import { Config } from '@shared/configs/config';
import {
  ProjectSummaryDocumentAttachmentView,
  ProjectSummaryDocumentGroupView,
  getProjectSummaryAllAllowedDocumentTypes,
  getProjectSummaryAllowedDocumentTypes,
  getProjectSummaryBoxNameForEntryType,
  getProjectSummaryEntries,
  getProjectSummarySectionTranslationParams,
  mapProjectSummaryEntryToItemView,
  updateProjectSummaryEntryAttachments
} from './project-summary-documents.helper';

@Component({
  selector: 'project-summary-documents',
  standalone: true,
  imports: [AngularSvgIconModule, TranslateModule, SharedModule, SwiperModule],
  templateUrl: './project-summary-documents.component.html',
  styleUrls: ['./project-summary-documents.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectSummaryDocumentsComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly projectStore = inject(ProjectStoreService);
  private readonly entryService = inject(ProjectCreatorEntryService);
  private readonly projectCreatorApi = inject(ProjectCreatorApiService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly translateService = inject(TranslateService);
  private readonly storage = inject(StorageService);
  private readonly attachmentService = inject(AttachmentService);
  private readonly dialog = inject(MatDialog);

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonTypes = ButtonTypes;
  protected readonly expandedDocumentGroups = signal<Record<'dataBox2' | 'dataBox3', boolean>>({
    dataBox2: true,
    dataBox3: true
  });
  protected readonly expandedSections = signal<Record<string, boolean>>({});
  protected readonly deletingEntryIds = signal<number[]>([]);
  protected readonly deletingAttachmentIds = signal<number[]>([]);
  protected readonly overflowingImageSliders = signal<Record<number, boolean>>({});
  protected readonly hoveredAttachmentId = signal<number | null>(null);
  protected readonly imagesSliderConfig = {
    slidesPerView: 'auto',
    spaceBetween: 12,
    navigation: false,
    watchOverflow: true,
    observer: true,
    observeParents: true
  };

  private readonly project = computed<Project | null>(() => this.projectStore.project() || null);
  protected readonly allAllowedDocumentTypes = computed<EntryType[]>(() => getProjectSummaryAllAllowedDocumentTypes());
  protected readonly documentGroups = computed<ProjectSummaryDocumentGroupView[]>(() =>
    ProjectSummaryDocumentsSectionsConfig.map((group) => ({
      boxName: group.boxName,
      title: group.title,
      description: group.description,
      hasDocuments: group.sections.some((section) =>
        this.getEntries(group.boxName).some(
          (entry) => entry.type === section.type && mapProjectSummaryEntryToItemView(entry).previewAttachments.length > 0
        )
      ),
      sections: group.sections
        .map((section) => ({
          ...section,
          translationParams: this.getSectionTranslationParams(section.type),
          items: this.getEntries(group.boxName)
            .filter((entry) => entry.type === section.type)
            .map((entry) => mapProjectSummaryEntryToItemView(entry))
            .filter((item) => item.previewAttachments.length > 0)
        }))
        .filter((section) => section.items.length > 0)
    }))
  );

  constructor() {
    this.entryService.actionEmitter
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ action, payload }) => {
        if (!payload?.id) return;

        switch (action) {
          case ProjectEntryActionType.ADD:
            this.upsertEntry(payload);
            break;
          case ProjectEntryActionType.UPDATE:
            this.upsertEntry(payload);
            break;
          case ProjectEntryActionType.DELETE:
            this.removeEntry(payload.id);
            break;
          default:
            break;
        }
      });
  }

  protected createEntryWithAllDocumentTypes(): void {
    const project = this.project();
    if (!project?.id) return;

    new ProjectCreatorEntitySidenavController().createProjectEntity({
      id: project.id,
      projectPublicFormToken: project.projectPublicFormToken || null,
      allowedDocumentTypes: this.allAllowedDocumentTypes()
    });
  }

  protected editEntry(entryId: number): void {
    const project = this.project();
    if (!project?.id || !entryId) return;
    const boxName = this.findEntryBoxName(entryId);

    new ProjectCreatorEntitySidenavController().editProjectEntity({
      id: project.id,
      entryId,
      projectPublicFormToken: project.projectPublicFormToken || null,
      allowedDocumentTypes: this.getAllowedDocumentTypes(boxName)
    });
  }

  protected confirmDelete(entry: Entry): void {
    if (!entry?.id || this.isDeleting(entry.id)) return;

    new RemoveModalController().remove(() => this.deleteEntry(entry)).subscribe();
  }

  protected isDeleting(entryId: number): boolean {
    return this.deletingEntryIds().includes(entryId);
  }

  protected slideNext(swiper?: SwiperComponent): void {
    swiper?.swiperRef?.slideNext(100);
  }

  protected slidePrev(swiper?: SwiperComponent): void {
    swiper?.swiperRef?.slidePrev(100);
  }

  protected initImageSwiper(itemId: number, swiper: any): void {
    const updateOverflowState = () => {
      this.overflowingImageSliders.update((state) => ({
        ...state,
        [itemId]: !swiper?.isLocked
      }));
    };

    queueMicrotask(updateOverflowState);
    swiper.on?.('lock', updateOverflowState);
    swiper.on?.('unlock', updateOverflowState);
    swiper.on?.('resize', updateOverflowState);
    swiper.on?.('observerUpdate', updateOverflowState);
    swiper.on?.('update', updateOverflowState);
  }

  protected showSliderNavigation(itemId: number): boolean {
    return !!this.overflowingImageSliders()[itemId];
  }

  protected toggleGroupVisibility(boxName: 'dataBox2' | 'dataBox3', value: boolean): void {
    this.expandedDocumentGroups.update((state) => ({
      ...state,
      [boxName]: value
    }));

    const group = this.documentGroups().find((item) => item.boxName === boxName);
    if (!group) return;

    const nextExpandedSections = group.sections.reduce<Record<string, boolean>>((acc, section) => {
      acc[this.getSectionKey(boxName, section.type)] = value;
      return acc;
    }, {});

    this.expandedSections.update((state) => ({
      ...state,
      ...nextExpandedSections
    }));
  }

  protected isGroupExpanded(boxName: 'dataBox2' | 'dataBox3'): boolean {
    return !!this.expandedDocumentGroups()[boxName];
  }

  protected toggleSectionVisibility(boxName: 'dataBox2' | 'dataBox3', type: EntryType): void {
    const sectionKey = this.getSectionKey(boxName, type);

    this.expandedSections.update((state) => ({
      ...state,
      [sectionKey]: !this.isSectionExpanded(boxName, type)
    }));
  }

  protected isSectionExpanded(boxName: 'dataBox2' | 'dataBox3', type: EntryType): boolean {
    const sectionKey = this.getSectionKey(boxName, type);
    const value = this.expandedSections()[sectionKey];
    return value === undefined ? this.isGroupExpanded(boxName) : value;
  }

  protected getGalleryId(itemId: number): string {
    return `project-summary-documents-${itemId}`;
  }

  protected getSectionKey(boxName: 'dataBox2' | 'dataBox3', type: EntryType): string {
    return `${boxName}-${type}`;
  }

  protected attachmentTrackBy(index: number, attachment: ProjectSummaryDocumentAttachmentView): number | string {
    return attachment.id || attachment.file || index;
  }

  protected onAttachmentMouseEnter(attachmentId: number): void {
    if (this.attachmentHoverTimeout) {
      clearTimeout(this.attachmentHoverTimeout);
    }

    this.attachmentHoverTimeout = window.setTimeout(() => {
      this.hoveredAttachmentId.set(attachmentId);
      this.attachmentHoverTimeout = null;
    }, 220);
  }

  protected onAttachmentMouseLeave(attachmentId: number): void {
    if (this.attachmentHoverTimeout) {
      clearTimeout(this.attachmentHoverTimeout);
      this.attachmentHoverTimeout = null;
    }

    if (this.hoveredAttachmentId() === attachmentId) {
      this.hoveredAttachmentId.set(null);
    }
  }

  protected isAttachmentHovered(attachmentId: number): boolean {
    return this.hoveredAttachmentId() === attachmentId;
  }

  protected isAttachmentDeleting(attachmentId: number): boolean {
    return this.deletingAttachmentIds().includes(attachmentId);
  }

  protected confirmDeleteAttachment(event: Event, entryId: number, attachment: ProjectSummaryDocumentAttachmentView): void {
    event.preventDefault();
    event.stopPropagation();

    if (!attachment?.id || this.isAttachmentDeleting(attachment.id)) return;

    new RemoveModalController().remove(() => this.deleteAttachment(entryId, attachment.id)).subscribe();
  }

  protected onAttachmentEditClick(
    event: Event,
    entryId: number,
    attachment: ProjectSummaryDocumentAttachmentView
  ): void {
    event.preventDefault();
    event.stopPropagation();

    if (!attachment?.id) return;

    const dialogRef = this.dialog.open(DocumentFileNameModalComponent, {
      width: Config.DEFAULT_MODAL_WIDTH,
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: true,
      panelClass: 'project-summary-document-file-name-modal-panel',
      data: {
        fileName: attachment.name || ''
      }
    });

    dialogRef.afterClosed().subscribe((fileName?: string) => {
      const normalizedFileName = fileName?.trim();
      if (!normalizedFileName || normalizedFileName === attachment.name) return;

      this.renameAttachment(entryId, attachment.id, normalizedFileName);
    });
  }

  private attachmentHoverTimeout: number | null = null;

  private deleteEntry(entry: Entry): void {
    this.setDeleting(entry.id, true);

    this.projectCreatorApi
      .deleteEntryInternal(entry.id)
      .pipe(finalize(() => this.setDeleting(entry.id, false)))
      .subscribe({
        next: () => {
          this.removeEntry(entry.id);
          this.snackBarService.success(this.translateService.instant('ProjectCreator.Sidenav.successDelete'));
        },
        error: () => {
          this.snackBarService.error(this.translateService.instant('ProjectCreator.Sidenav.errorDelete'));
        }
      });
  }

  private deleteAttachment(entryId: number, attachmentId: number): void {
    this.setDeletingAttachment(attachmentId, true);

    this.attachmentService
      .removeAttachment(attachmentId)
      .pipe(finalize(() => this.setDeletingAttachment(attachmentId, false)))
      .subscribe({
        next: () => {
          this.removeAttachmentFromEntry(entryId, attachmentId);
          this.snackBarService.success(this.translateService.instant('ProjectCreator.Sidenav.successDelete'));
        },
        error: () => {
          this.snackBarService.error(this.translateService.instant('ProjectCreator.Sidenav.errorDelete'));
        }
      });
  }

  private renameAttachment(entryId: number, attachmentId: number, fileName: string): void {
    this.setDeletingAttachment(attachmentId, true);

    this.attachmentService
      .renameAttachment(attachmentId, fileName)
      .pipe(finalize(() => this.setDeletingAttachment(attachmentId, false)))
      .subscribe({
        next: () => {
          this.renameAttachmentInEntry(entryId, attachmentId, fileName);
          this.snackBarService.success(this.translateService.instant('ProjectCreator.Sidenav.successEdit'));
        },
        error: () => {
          this.snackBarService.error(this.translateService.instant('ProjectCreator.Sidenav.errorEdit'));
        }
      });
  }

  private upsertEntry(entryLike: Entry): void {
    const incomingEntry = new Entry(entryLike);
    const project = this.project();
    if (!project) return;

    const boxName = getProjectSummaryBoxNameForEntryType(incomingEntry.type);
    const nextEntries = [...(project[boxName]?.entries || [])];
    const existingIndex = nextEntries.findIndex((item) => item.id === incomingEntry.id);

    if (existingIndex > -1) {
      nextEntries[existingIndex] = incomingEntry;
    } else {
      nextEntries.unshift(incomingEntry);
    }

    this.setProjectEntries(boxName, nextEntries);
  }

  private removeEntry(entryId: number): void {
    const project = this.project();
    if (!project) return;

    const boxName = this.findEntryBoxName(entryId);
    this.setProjectEntries(
      boxName,
      (project[boxName]?.entries || []).filter((entry) => entry.id !== entryId)
    );
  }

  private setProjectEntries(boxName: 'dataBox2' | 'dataBox3', entries: Entry[]): void {
    const project = this.project();
    if (!project) return;

    const updatedProject = new Project({
      ...project,
      [boxName]: {
        ...project[boxName],
        entries
      }
    });

    this.projectStore.project.set(updatedProject);
    this.projectStore.projectCtrl.setProject(updatedProject);
  }

  private setDeleting(entryId: number, isDeleting: boolean): void {
    this.deletingEntryIds.update((ids) =>
      isDeleting ? [...ids, entryId] : ids.filter((currentId) => currentId !== entryId)
    );
  }

  private setDeletingAttachment(attachmentId: number, isDeleting: boolean): void {
    this.deletingAttachmentIds.update((ids) =>
      isDeleting ? [...ids, attachmentId] : ids.filter((currentId) => currentId !== attachmentId)
    );
  }

  private removeAttachmentFromEntry(entryId: number, attachmentId: number): void {
    const project = this.project();
    if (!project) return;

    const boxName = this.findEntryBoxName(entryId);
    const nextEntries = updateProjectSummaryEntryAttachments(
      project[boxName]?.entries || [],
      entryId,
      (attachment) => (attachment.id === attachmentId ? null : attachment)
    );

    this.hoveredAttachmentId.set(null);
    this.setProjectEntries(boxName, nextEntries);
  }

  private renameAttachmentInEntry(entryId: number, attachmentId: number, fileName: string): void {
    const project = this.project();
    if (!project) return;

    const boxName = this.findEntryBoxName(entryId);
    const nextEntries = updateProjectSummaryEntryAttachments(
      project[boxName]?.entries || [],
      entryId,
      (attachment) => {
        if (attachment.id !== attachmentId) return attachment;

        const nextAttachment = new Attachment(attachment);
        nextAttachment.name = fileName;
        return nextAttachment;
      }
    );

    this.hoveredAttachmentId.set(null);
    this.setProjectEntries(boxName, nextEntries);
  }

  protected getAttachmentToken(): string | null {
    return this.project()?.projectPublicFormToken || null;
  }

  private getEntries(boxName: 'dataBox2' | 'dataBox3'): Entry[] {
    return getProjectSummaryEntries(this.project()?.[boxName]?.entries || []);
  }

  private getSectionTranslationParams(type: EntryType): Record<string, string> | undefined {
    return getProjectSummarySectionTranslationParams(type, this.storage?.Company?.name || '');
  }

  private findEntryBoxName(entryId: number): 'dataBox2' | 'dataBox3' {
    const project = this.project();
    if (project?.dataBox3?.entries?.some((entry) => entry.id === entryId)) {
      return 'dataBox3';
    }

    return 'dataBox2';
  }

  private getAllowedDocumentTypes(boxName: 'dataBox2' | 'dataBox3'): EntryType[] {
    return getProjectSummaryAllowedDocumentTypes(boxName);
  }
}
